import React from "react";
import { Grid, Card, CardContent, Typography, Box, Skeleton, LinearProgress } from "@mui/material";
import { Users, BookOpen, Inbox, Flame, TrendingUp, Clock, ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import AdminShell from "../layout/AdminShell";
import { adminGetLeadStats } from "../../api/leads";
import { adminGetCourses } from "../../api/courses";

function StatCard({ label, value, icon: Icon, color, sub, loading }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        transition: "transform 0.22s cubic-bezier(.2,.8,.2,1), box-shadow 0.22s ease, border-color 0.22s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 20px 40px -22px rgba(15,23,42,0.18)",
          borderColor: `${color}40`,
        },
      }}
    >
      <CardContent sx={{ p: 2.75 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              bgcolor: `${color}16`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={19} color={color} strokeWidth={2.1} />
          </Box>
        </Box>

        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
          {label}
        </Typography>

        {loading ? (
          <Skeleton variant="text" width={64} height={38} />
        ) : (
          <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            {value ?? "—"}
          </Typography>
        )}

        {sub && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.6 }}>{sub}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

const pipelineStages = [
  { key: "new", label: "New", color: "#94A3B8" },
  { key: "contacted", label: "Contacted", color: "#2563EB" },
  { key: "follow_up", label: "Follow-up", color: "#F59E0B" },
  { key: "qualified", label: "Qualified", color: "#06B6D4" },
  { key: "converted", label: "Converted", color: "#22C55E" },
  { key: "lost", label: "Lost", color: "#EF4444" },
];

export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["lead-stats"],
    queryFn: () => adminGetLeadStats().then(r => r.data),
  });

  const { data: courses, isLoading: coursesLoading } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: () => adminGetCourses().then(r => r.data),
  });

  const loading = statsLoading || coursesLoading;

  const statCards = [
    { label: "Total Leads",   value: stats?.total,     icon: Inbox,      color: "#2563EB" },
    { label: "New",           value: stats?.new,       icon: Clock,      color: "#8B5CF6" },
    { label: "Hot Leads",     value: stats?.hot,       icon: Flame,      color: "#EF4444" },
    { label: "Converted",     value: stats?.converted, icon: TrendingUp, color: "#22C55E" },
    { label: "Total Courses", value: courses?.length,  icon: BookOpen,   color: "#F59E0B" },
    { label: "Qualified",     value: stats?.qualified, icon: Users,      color: "#06B6D4" },
  ];

  const pipelineTotal = stats
    ? pipelineStages.reduce((sum, s) => sum + (stats[s.key] ?? 0), 0)
    : 0;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <AdminShell title="Dashboard">
      {/* Welcome banner */}
      <Box
        sx={{
          position: "relative",
          borderRadius: "18px",
          p: { xs: 2.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #0A0F1E 0%, #111A33 100%)",
          color: "white",
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -60,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(37,99,235,0.3) 0%, transparent 70%)",
          }}
        />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.55)", fontWeight: 600, mb: 0.5 }}>
              {greeting}
            </Typography>
            <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 700, letterSpacing: "-0.01em" }}>
              Here's what's happening across NetLabs ITS today.
            </Typography>
          </Box>
          {stats && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "999px", px: 1.75, py: 0.75 }}>
              <ArrowUpRight size={14} color="#22C55E" />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {stats.converted ?? 0} leads converted so far
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2}>
        {statCards.map((s) => (
          <Grid item xs={12} sm={6} xl={4} key={s.label}>
            <StatCard {...s} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* Lead pipeline */}
      <Card sx={{ mt: 3, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Lead Pipeline</Typography>
            {stats && (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 600 }}>
                {pipelineTotal} total leads
              </Typography>
            )}
          </Box>

          {statsLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={14} />
              ))}
            </Box>
          ) : stats ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {pipelineStages.map(({ key, label, color }) => {
                const value = stats[key] ?? 0;
                const pct = pipelineTotal > 0 ? Math.round((value / pipelineTotal) * 100) : 0;
                return (
                  <Box key={key}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.6 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>{label}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 600 }}>
                        {value} <Box component="span" sx={{ color: "rgba(15,23,42,0.35)" }}>({pct}%)</Box>
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "rgba(15,23,42,0.06)",
                        "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: color },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography color="text.secondary" fontSize={14}>No lead data available yet.</Typography>
          )}
        </CardContent>
      </Card>
    </AdminShell>
  );
}